import React from 'react';

import Chip from '@mui/material/Chip';
import Link, { LinkProps } from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface ActionChipProps {
  icon?: React.ReactElement;
  text?: string | null;
  linkLabel?: string;
  linkSx?: LinkProps['sx'];
  textColor?: string;
  backgroundColor?: string;
  disabled?: boolean;
  disabledLink?: boolean;
  onLinkClick?: () => void;
}

const ActionChip = ({
  text = undefined,
  icon = undefined,
  backgroundColor = 'secondary.light',
  textColor = 'primary.main',
  linkLabel = undefined,
  linkSx = undefined,
  disabled = false,
  disabledLink = false,
  onLinkClick = undefined,
}: ActionChipProps) => {
  const showLink = Boolean(linkLabel && onLinkClick);
  const isLinkDisabled = disabled || disabledLink;
  const linkClickHandler = isLinkDisabled ? undefined : onLinkClick;
  const linkTextColor = isLinkDisabled ? 'action.disabled' : textColor;

  return (
    <Chip
      icon={
        icon &&
        React.cloneElement(icon, {
          style: { ...icon.props.style },
        })
      }
      size="small"
      disabled={disabled}
      label={
        <Stack direction="row" spacing={1} alignItems="center">
          {text && (
            <Typography variant="h5" color={textColor} fontWeight={500}>
              {text}
            </Typography>
          )}
          {showLink && (
            <Link
              component="button"
              onClick={linkClickHandler}
              underline="always"
              color={linkTextColor}
              disabled={isLinkDisabled}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textDecorationThickness: 1,
                '&:hover': {
                  color: linkTextColor,
                },
                ...linkSx,
              }}
            >
              {linkLabel}
            </Link>
          )}
        </Stack>
      }
      sx={{
        backgroundColor,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
  );
};

export default ActionChip;
